import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import {
	fetchESAuditLogs,
	fetchAccountDownloadESAudit,
	fetchAggregatedESAuditLogs,
} from './thunks';
import {
	AuditLogAggregations,
	AuditLogResponse,
	AuditLogsFilter,
	AuditLogState,
} from 'src/types/auditLogTypes';

const initialState: AuditLogState = {
	tableData: null,
	loading: false,
	error: null,
	page: 1,
	pageSize: 25,
	totalCount: 0,
	count: 0,
	searchText: '',
	searchField: '',
	sort: 'creationTime',
	sortDir: 'desc',
	dateRangeStart: DateTime.local()
		.minus({ days: 30 })
		.startOf('day')
		.toUTC()
		.toISO(),
	dateRangeEnd: DateTime.local().endOf('day').toUTC().toISO(),
	filters: [],
	filtersAggregation: null,
	toast: {
		message: '',
		type: 'success',
	},
};

const auditLogsSlice = createSlice({
	name: 'auditLogs',
	initialState,
	reducers: {
		resetAuditLogsState: () => initialState,
		updatePage: (state, action: PayloadAction<number>) => {
			state.page = action.payload;
		},
		updatePageSize: (state, action: PayloadAction<number>) => {
			state.pageSize = action.payload;
		},
		updateDateRange: (
			state,
			action: PayloadAction<{ start: string | null; end: string | null }>,
		) => {
			state.dateRangeStart = action.payload.start;
			state.dateRangeEnd = action.payload.end;
		},
		updateSearchTextAndField: (
			state,
			action: PayloadAction<{ text: string; field: string }>,
		) => {
			state.searchText = action.payload.text;
			state.searchField = action.payload.field;
		},
		updateSort: (
			state,
			action: PayloadAction<{ sort: string; sortDir: string }>,
		) => {
			state.sort = action.payload.sort;
			state.sortDir = action.payload.sortDir;
		},
		pushToast: (
			state,
			action: PayloadAction<{
				message: string;
				type: 'info' | 'success' | 'error' | 'warning' | 'default';
			}>,
		) => {
			state.toast = { ...state.toast, ...action.payload };
		},
		updateFilters: (state, action: PayloadAction<AuditLogsFilter[]>) => {
			state.filters = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchESAuditLogs.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				fetchESAuditLogs.fulfilled,
				(state, action: PayloadAction<AuditLogResponse>) => {
					state.loading = false;
					state.tableData = action.payload.data;
					state.error = null;
					state.totalCount = action.payload.totalCount;
					state.count = action.payload.count ?? 0;
				},
			)
			.addCase(fetchESAuditLogs.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(fetchAccountDownloadESAudit.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchAccountDownloadESAudit.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(fetchAccountDownloadESAudit.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			.addCase(fetchAggregatedESAuditLogs.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				fetchAggregatedESAuditLogs.fulfilled,
				(state, action: PayloadAction<AuditLogAggregations>) => {
					state.loading = false;
					state.filtersAggregation = action.payload;
					state.error = null;
				},
			)
			.addCase(fetchAggregatedESAuditLogs.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			});
	},
});

export const {
	resetAuditLogsState,
	updatePageSize,
	updatePage,
	updateDateRange,
	updateSearchTextAndField,
	updateSort,
	pushToast,
	updateFilters,
} = auditLogsSlice.actions;
export default auditLogsSlice.reducer;
